import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import type { ScatterPlotCommonProps, ScatterPlotDatum, ScatterPlotRawSerie } from "@nivo/scatterplot"
import { Typography } from "@synapse-analytics/synapse-ui"

import { KonanEmptyState } from "../../utility/KonanEmptyState"
import type { ChartsContainerProps } from "../ChartsContainer"
import { ChartsContainer } from "../ChartsContainer"
import { BaseScatterPlot } from "./BaseScatterPlot"

interface ScatterPlotProps extends Omit<ChartsContainerProps, "children" | "emptyState"> {
  title: string // Title of the chart
  subHeader?: React.ReactElement // Optional subheader to be displayed above the chart
  data: ScatterPlotRawSerie<{
    x: number
    y: string
    bin: string
    decision?: string
    reason?: string
  }>[] // Data to be plotted in the scatter plot
  isLoading: boolean // Flag to indicate whether the data is still loading
  graphHeight: number // Height of the graph (used for layout)
  emptyState?: { title: string; subTitle?: string } // Optional empty state details to show when there's no data
  chartEmptyState?: { title: string; subTitle?: string } // Optional empty state for the chart part to show when there is no data
  axisTop?: ScatterPlotCommonProps<ScatterPlotDatum>["axisTop"]
}

/**
 * ScatterPlot component that renders a scatter plot chart using Nivo's `ResponsiveScatterPlot`.
 *
 * This component visualizes a set of data points and allows customization through props like title, subheader,
 * and optional empty state configuration.
 *
 * @property {string} title - The title of the scatter plot chart.
 * @property {React.ReactElement} [subHeader] - Optional subheader that appears above the chart.
 * @property {ScatterPlotRawSerie<{x: number; y: string; bin: string; decision?: string; reason?: string }>[]} data - The data to be plotted on the scatter plot. It includes an `id` and an array of data points with `x`, `y`, `bin`, and optional `decision` and `reason`.
 * @property {boolean} isLoading - Flag indicating whether the data is still being loaded.
 * @property {number} graphHeight - The height of the graph in pixels.
 * @property {{ title: string; subTitle?: string }} [emptyState] - Optional configuration for the empty state when there is no data to display.
 * @returns {React.ReactElement} The rendered scatter plot chart.
 */
export function ScatterPlot(props: Readonly<ScatterPlotProps>): React.ReactElement {
  const { t } = useTranslation("graphs")
  const { data, title, graphHeight, isLoading, subHeader, emptyState, tooltipText, axisTop } = props

  // Render the BoxPlot component
  return (
    <ChartsContainer title={title} isLoading={isLoading} graphHeight={graphHeight + 80} tooltipText={tooltipText}>
      {emptyState ? (
        <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
          <KonanEmptyState
            title={emptyState?.title ?? `${title} ${t("scatterPlot.emptyState.unavailable")}`}
            subTitle={emptyState?.subTitle}
          />
        </Grid>
      ) : (
        <Grid container p={2} gap={1}>
          <Grid item xs={12}>
            {subHeader}
          </Grid>
          <Grid
            container
            item
            xs={12}
            style={{ height: graphHeight, padding: "8px 0px 0px" }}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {data.length === 0 ? (
              <KonanEmptyState
                title={t("scatterPlot.emptyState.couldntPlot", { title })}
                subTitle={t("scatterPlot.emptyState.selectOtherFilters")}
              />
            ) : (
              <BaseScatterPlot
                data={data}
                margin={{ top: 20, right: 20, bottom: 60, left: 120 }}
                axisTop={axisTop}
                renderTickFn={(tick) => {
                  return (
                    <foreignObject width={112} height={30} transform={`translate(${tick.x - 120},${tick.y - 10})`}>
                      <div title={t("scatterPlot.tooltip.title", { value: tick.value })}>
                        <Typography variant="p" style={{ textAlign: "right" }}>
                          {tick.value.length > 13 ? tick.value.slice(0, 13) + "..." : tick.value}
                        </Typography>
                      </div>
                    </foreignObject>
                  )
                }}
              />
            )}
          </Grid>
        </Grid>
      )}
    </ChartsContainer>
  )
}
